// Shared "Color tokens" field group — the eight semantic color inputs plus
// the live WCAG contrast preview. Consumed by both PartnerBrandCreateWizard
// (step 3) and PartnerBrandEditPage. Controlled: the container owns the
// colors + the guardrail `messages` (so a submit-time server 400 and the
// "Check contrast" preview flow into the same display).

import { useState } from "react";
import type {
  PartnerBrandColors,
  PartnerBrandValidationMessage,
} from "@/api/partnerBrands";
import { partnerBrandsApi } from "@/api/partnerBrands";
import { Badge, Button, Input, Label } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { COLOR_FIELDS, swatch } from "./shared";

export interface PartnerBrandColorsSectionProps {
  colors: PartnerBrandColors;
  onColorChange: (key: keyof PartnerBrandColors, value: string) => void;
  messages: PartnerBrandValidationMessage[];
  onMessagesChange: (messages: PartnerBrandValidationMessage[]) => void;
}

export function PartnerBrandColorsSection({
  colors,
  onColorChange,
  messages,
  onMessagesChange,
}: PartnerBrandColorsSectionProps) {
  const toast = useToast();
  const [checking, setChecking] = useState(false);

  const errors = messages.filter((m) => m.severity === "error");
  const warnings = messages.filter((m) => m.severity === "warning");

  const previewContrast = async () => {
    setChecking(true);
    try {
      const result = await partnerBrandsApi.validate(colors);
      onMessagesChange(result.messages);
      if (result.messages.length === 0)
        toast.success("Colors pass every contrast target.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <section className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between gap-space-3">
        <h2 className="font-display font-bold text-h3 m-0">Color tokens</h2>
        <Button
          type="button"
          variant="secondary"
          onClick={previewContrast}
          loading={checking}
        >
          Check contrast
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
        {COLOR_FIELDS.map((f) => (
          <div key={f.key} className="flex flex-col gap-space-1">
            <Label htmlFor={`pb-color-${f.key}`}>{f.label}</Label>
            <div className="flex items-center gap-space-2">
              <input
                type="color"
                aria-label={`${f.label} swatch`}
                className="w-9 h-9 rounded-sm border border-border-light bg-white-pure p-0"
                value={swatch(colors[f.key])}
                onChange={(e) => onColorChange(f.key, e.target.value.toUpperCase())}
              />
              <Input
                id={`pb-color-${f.key}`}
                value={colors[f.key]}
                className="font-mono"
                placeholder="#000000"
                onChange={(e) => onColorChange(f.key, e.target.value)}
              />
            </div>
            <p className="text-caption text-gray-mid m-0">{f.help}</p>
          </div>
        ))}
      </div>

      {errors.length > 0 && (
        <div className="flex flex-col gap-space-1 border border-status-error rounded-sm p-space-3">
          <div className="flex items-center gap-space-2">
            <Badge status="error">Errors</Badge>
            <span className="text-small font-medium">These block saving.</span>
          </div>
          <ul className="list-disc pl-space-4 text-small text-status-error m-0">
            {errors.map((m, i) => (
              <li key={`e-${i}`}>{m.message}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="flex flex-col gap-space-1 border border-status-warning rounded-sm p-space-3">
          <div className="flex items-center gap-space-2">
            <Badge status="review">Warnings</Badge>
            <span className="text-small font-medium">Advisory — you can still save.</span>
          </div>
          <ul className="list-disc pl-space-4 text-small text-gray-mid m-0">
            {warnings.map((m, i) => (
              <li key={`w-${i}`}>{m.message}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
